import type { ChakraProps, FlexProps } from '@chakra-ui/react';
import { Box, Button, Flex, Radio, Text } from '@chakra-ui/react';
import type { BigNumber } from '@ethersproject/bignumber';
import type { UseQueryResult } from '@tanstack/react-query';
import { Card } from '@ui/components/Card';
import { RenderInDesktop } from '@ui/components/MobileOrDesktop';
import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerBody,
  ModalOrDrawerCloseButton,
  ModalOrDrawerFooter,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@ui/components/ModalOrDrawer';
import { RotatingBg } from '@ui/components/RotatingBg';
import { useTranslations } from '@ui/i18n';
import { formatTokenAmount } from '@ui/utils/format';
import type { Dispatch, FC, SetStateAction } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';

type TokenSelectionDrawerProps<T extends string> = {
  onClose: () => void;
  isOpen: boolean;
  value: T;
  preferred?: T;
  onChange: (token: T) => void;
  tokenList: T[];
  tokenBalanceDict: Record<T, UseQueryResult<BigNumber>>;
  tokenDict: Record<
    T,
    {
      name: string;
      Icon: FC<ChakraProps>;
      decimals?: number;
    }
  >;
  skipConfirmation?: boolean;
  theme?: 'vvs';
} & Omit<FlexProps, 'onChange'>;

export const TokenSelectionDrawer = <T extends string>({
  onClose,
  isOpen,
  value,
  onChange,
  tokenList,
  tokenBalanceDict,
  tokenDict,
  skipConfirmation,
  preferred,
  theme,
  ...flexProps
}: TokenSelectionDrawerProps<T>) => {
  const t = useTranslations();

  const [checkedToken, setCheckedToken] = useState<T>(value);
  const defaultTokenList = useMemo(
    () => (!preferred ? tokenList : tokenList.filter((t) => t !== preferred)),
    [preferred, tokenList],
  );

  useEffect(() => {
    if (!isOpen) {
      setCheckedToken(value);
    }
  }, [isOpen, value]);

  return (
    <ModalOrDrawer
      placement="bottom"
      size={{
        base: 'full',
        desktop: 'md',
      }}
      isOpen={isOpen}
      onClose={() => onClose()}
      scrollBehavior="inside"
    >
      <ModalOrDrawerOverlay />
      <ModalOrDragDrawerContent
        bgColor={theme === 'vvs' ? 'vvs.bg' : 'bg.card'}
        {...flexProps}
      >
        <ModalOrDrawerHeader
          color={theme === 'vvs' ? 'text.vvs' : 'text.normal'}
        >
          {t('Select token')}
        </ModalOrDrawerHeader>
        <RenderInDesktop>
          <ModalOrDrawerCloseButton />
        </RenderInDesktop>
        <ModalOrDrawerBody>
          <Flex gap="16px" direction="column">
            {(preferred
              ? [preferred, ...defaultTokenList]
              : defaultTokenList
            ).map((token, i) => {
              const tokenInfo = tokenDict[token];
              const isChecked = token === checkedToken;
              const balanceRet = tokenBalanceDict[token];

              return (
                <Fragment key={token}>
                  {preferred && i === 0 && <Box>{t('Preferred')}</Box>}
                  {theme === 'vvs' ? (
                    <TokenSelectionCardVvs
                      token={token}
                      isChecked={isChecked}
                      skipConfirmation={!!skipConfirmation}
                      onClose={onClose}
                      onChange={onChange}
                      tokenInfo={tokenInfo}
                      balanceRet={balanceRet}
                      setCheckedToken={setCheckedToken}
                    />
                  ) : (
                    <TokenSelectionCard
                      token={token}
                      isChecked={isChecked}
                      skipConfirmation={!!skipConfirmation}
                      onClose={onClose}
                      onChange={onChange}
                      tokenInfo={tokenInfo}
                      balanceRet={balanceRet}
                      setCheckedToken={setCheckedToken}
                    />
                  )}
                  {preferred && i === 0 && <Box>{t('Other')}</Box>}
                </Fragment>
              );
            })}
          </Flex>
        </ModalOrDrawerBody>
        {!skipConfirmation && (
          <ModalOrDrawerFooter>
            <Button
              w="full"
              onClick={() => {
                onChange(checkedToken);
                onClose();
              }}
            >
              {t('Confirm')}
            </Button>
          </ModalOrDrawerFooter>
        )}
      </ModalOrDragDrawerContent>
    </ModalOrDrawer>
  );
};

type TokenSelectionCardProps<T extends string> = {
  token: T;
  isChecked: boolean;
  skipConfirmation: boolean;
  onClose: () => void;
  onChange: (token: T) => void;
  tokenInfo: {
    name: string;
    Icon: FC<ChakraProps>;
    decimals?: number;
  };
  balanceRet: UseQueryResult<BigNumber>;
  setCheckedToken: Dispatch<SetStateAction<T>>;
};

const TokenSelectionCard = <T extends string>({
  token,
  isChecked,
  skipConfirmation,
  onClose,
  onChange,
  tokenInfo,
  balanceRet,
  setCheckedToken,
}: TokenSelectionCardProps<T>) => {
  const t = useTranslations();

  return (
    <Card
      cursor="pointer"
      display="flex"
      mx="0"
      my="0"
      p="16px"
      gap="8px"
      isActive={isChecked}
      onClick={
        skipConfirmation
          ? () => {
              onChange(token);
              onClose();
            }
          : () => setCheckedToken(token)
      }
    >
      <RotatingBg w="48px" h="48px" justify="center" alignItems="center">
        <tokenInfo.Icon width="32px" height="32px" />
      </RotatingBg>
      <Box flex="1">
        <Text textStyle="h4Bold">{tokenInfo.name}</Text>
        <Text mt="4px" color="text-light">
          {t('Wallet balance')}:{' '}
          <Text as="span" textStyle="bodyBold">
            {balanceRet.data
              ? formatTokenAmount(balanceRet.data, 2, tokenInfo.decimals)
              : '--'}
          </Text>
        </Text>
      </Box>
      <Radio isChecked={isChecked} alignSelf="flex-start" />
    </Card>
  );
};

const TokenSelectionCardVvs = <T extends string>({
  token,
  isChecked,
  skipConfirmation,
  onClose,
  onChange,
  tokenInfo,
  balanceRet,
  setCheckedToken,
}: TokenSelectionCardProps<T>) => {
  const t = useTranslations();

  return (
    <Card
      cursor="pointer"
      display="flex"
      mx="auto"
      minW="255px"
      my="0"
      p="16px"
      gap="8px"
      isActive={isChecked}
      variant={'vvs'}
      onClick={
        skipConfirmation
          ? () => {
              onChange(token);
              onClose();
            }
          : () => setCheckedToken(token)
      }
    >
      <Flex flexDir="column">
        <Flex gap={1} align="center">
          <tokenInfo.Icon width="32px" height="32px" />{' '}
          <Text textStyle="h4Bold" color="text.vvs">
            {tokenInfo.name}
          </Text>
        </Flex>
        <Text mt="4px" color="vvs.textLight">
          {t('Wallet balance')}:{' '}
          <Text as="span" textStyle="bodyBold">
            {balanceRet.data
              ? formatTokenAmount(balanceRet.data, 2, tokenInfo.decimals)
              : '--'}
          </Text>
        </Text>
      </Flex>
    </Card>
  );
};
